import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import {
  update,
  exportContentTemplatesToCSV,
  setArchived,
  paginatePropertyContentTemplates,
} from '@go-client/lib/api/contentTemplate';
import {
  ALL_CONTENT_TEMPLATE_TYPES,
  BareContentTemplate,
  ContentTemplateType,
} from '@go-shared/types/contentTemplate';
import {
  BubbleAccordion,
  BubbleBadge,
  BubbleButton,
  BubbleDebounce,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleTable,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

import './ContentLibrary.scss';
import Libraries from './Libraries';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canManageLibraries, canModifyMultiPropertyItem } from '@go-shared/lib/permissions';
import { BubbleTableFields, BubbleTableTemplate } from '@go-client/components/BubbleUI/BubbleTable';
import { PropertyTag } from '@go-shared/types/property';
import { hashStringToColor } from '@go-shared/lib/utils';
import { getLocaleUTCDateTimeString, parseDate } from '@go-shared/lib/time';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { CopyToClipboardButton } from '@go-client/components/CopyToClipboard';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import ContentTemplateFormik, {
  ContentTemplateFormikMode,
} from '@go-client/components/Formik/ContentTemplateFormik';
import BubbleDrawerBasicContainer from '@go-client/components/BubbleUI/BubbleDrawerHeader';
import { LibrariesSubNavigation } from '@go-client/components/Navigations/LibrariesSubNavigation';
import AccessGlanceButton from '@go-client/components/AccessGlanceButton';
import PropertyAccessFormik from '@go-client/components/Formik/PropertyAccessFormik';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import { SortOrder } from '@go-shared/types/userActionRecord';
import { DEFAULT_PER_PAGE, PaginationMetadata } from '@go-shared/types/pagination';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSortingIcon } from '@go-shared/lib/paginations';
import { Tooltip } from '@spark-labs/avocado-ui';
import { McmEndpoints } from '@go-client/components/Mcm/McmEndpoints';
import { readQs } from '@go-shared/lib/querystring';
import { isNotBasicContentTemplate } from '@go-shared/lib/contentTemplate';
import { stripHTML } from '@go-shared/lib/string';

interface ContentLibraryDataBundle {
  tableItems: ContentTableItem[];
}

interface ContentTableItem {
  id: string;
  type: ContentTemplateType;
  name: string;
  value: string;
  updatedAt: string;
  tags: PropertyTag[];
  _source: BareContentTemplate;
}

type ContentTemplateFilters = {
  tags: Array<BubbleTypeaheadValue<PropertyTag>>;
  types: Array<BubbleTypeaheadValue<ContentTemplateType>>;
};

type PaginationParams<T> = Partial<ContentTemplateFilters> & {
  page: number;
  perPage: number;
  sortBy: keyof T;
  sortDirection: SortOrder;
  archived: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  propertyId?: string;
};

export default function ContentLibrary(props: RouteComponentProps) {
  const { ...rest } = props;

  const { activeProperty: property } = useActiveProperty();
  const serverInfo = useServerInfo();

  const { user } = useUserInfo();

  const tagOpts = property.contentTags.map(t => ({
    key: t._id,
    item: t,
  }));
  const typeOpts = ALL_CONTENT_TEMPLATE_TYPES.map(t => ({ key: t, item: t }));
  const tagMap = mapBy(tagOpts, 'key');

  const [loading, setLoading] = useState(false);
  const [dataBundle, setDataBundle] = useState<ContentLibraryDataBundle | undefined>(undefined);
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );

  const [mode, setMode] = useState<ContentTemplateFormikMode | undefined>(undefined);
  const [showAccessForm, setShowAccessForm] = useState<BareContentTemplate | undefined>(undefined);
  const [filters, setFilters] = useState<ContentTemplateFilters>({
    tags: [],
    types: [],
  });
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});

  const queries = readQs();

  const [paginationParams, setPaginationParams] = useState<PaginationParams<BareContentTemplate>>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: 'createdAt',
    sortDirection: SortOrder.DESC,
    startDate: '',
    endDate: '',
    propertyId: property._id,
    archived: false,
    tags: [],
    types: [],
    search: typeof queries.search === 'string' ? queries.search : '',
  });

  const contentRef = useRef<HTMLDivElement | null>(null);
  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationParams.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationParams, contentRef]);

  useEffect(() => {
    if (property._id !== paginationParams.propertyId) {
      setPaginationParams({ ...paginationParams, propertyId: property._id });
    }
  }, [property]);

  async function refresh() {
    setLoading(true);
    await fetchDataBundle();
  }

  useEffect(() => {
    refresh();
  }, [paginationParams]);

  async function onSubmit() {
    setShowAccessForm(undefined);
    setMode(undefined);
    refresh();
  }

  async function fetchDataBundle() {
    try {
      const contentTemplates = await paginatePropertyContentTemplates({
        page: paginationParams.page,
        perPage: paginationParams.perPage,
        sortBy: paginationParams.sortBy,
        sortDirection: paginationParams.sortDirection,
        propertyId: property._id,
        ...(paginationParams.search && { search: paginationParams.search }),
        ...(paginationParams.archived && { archived: paginationParams.archived }),
        ...(paginationParams.tags && { tags: paginationParams.tags.map(t => t.item._id) }),
        ...(paginationParams?.types && {
          types: paginationParams.types.map(t => t.item),
        }),
        ...(paginationParams.startDate && { startDate: paginationParams.startDate }),
        ...(paginationParams.endDate && { endDate: paginationParams.endDate }),
      });

      setPaginationMetadata(contentTemplates.meta);

      const tableItems: ContentTableItem[] = contentTemplates.data.map(itm => {
        let updatedAt = '';

        if (itm.updatedAt) {
          const updatedAtDate = parseDate(itm.updatedAt);

          updatedAt = updatedAtDate ? getLocaleUTCDateTimeString(updatedAtDate) : itm.updatedAt;
        }

        const tags = itm.tags.map(t => tagMap[t]?.item).filter(notUndefined);

        return {
          id: itm._id,
          type: itm.type || 'basic',
          name: itm.name,
          value: itm.content,
          updatedAt,
          tags,
          _source: itm,
        };
      });

      setDataBundle({
        tableItems:
          paginationParams.page > 1
            ? [...(dataBundle?.tableItems ?? []), ...tableItems]
            : tableItems,
      });
    } finally {
      setLoading(false);
    }
  }

  function handleAddContentClick() {
    setMode({
      type: 'create',
    });
  }

  async function generateCSV() {
    const result = await exportContentTemplatesToCSV(property._id);
    if (result.location) {
      window.open(result.location, '_blank');
    }
  }

  async function handleFormArchive(item: BareContentTemplate, state: boolean) {
    await setArchived({
      ...item,
      archived: state,
    });

    // no need to re-fetch, only remove the archived one from list
    setDataBundle({
      ...dataBundle,
      tableItems: (dataBundle?.tableItems || []).filter(ti => ti._source._id !== item._id),
    });
  }

  const tableFields: Array<BubbleTableFields<ContentTableItem>> = [
    {
      key: 'access',
    },
    {
      key: 'name',
      tdProps: { style: { minWidth: '200px' } },
    },
    {
      key: 'value',
      label: 'Description',
      tdProps: { style: { minWidth: '400px' } },
    },
    { key: 'type', thProps: { className: 'pl-1-i' }, tdProps: { className: 'p-1-i' } },
    {
      key: 'tags',
      thProps: { className: 'pl-1-i' },
      tdProps: { className: 'p-1-i' },
    },
    {
      key: 'endpoints',
    },
    {
      key: 'createdAt',
      thProps: {
        onClick: () => {
          const sortDirection =
            paginationParams.sortBy === 'createdAt'
              ? paginationParams.sortDirection * -1
              : SortOrder.DESC;
          setPaginationParams({ ...paginationParams, page: 1, sortBy: 'createdAt', sortDirection });
        },
        className: 'whitespace-nowrap',
      },
      render: () => (
        <>
          <span className="cursor-pointer">
            {'Created'}
            {paginationParams.sortBy === 'createdAt' && (
              <FontAwesomeIcon
                icon={getSortingIcon(paginationParams.sortDirection)}
                fixedWidth
                size={'sm'}
                className={'cursor-pointer'}
              />
            )}
          </span>
        </>
      ),
    },
    {
      key: 'actions',
      label: '',
      thProps: { className: 'pl-4-i' },
      tdProps: { className: 'whitespace-nowrap p-1-i pr-4-i text-right' },
    },
  ].filter(notUndefined);

  const tableTemplates: Array<BubbleTableTemplate<ContentTableItem>> = [
    {
      key: 'name',
      render: item => {
        return (
          <>
            <div>{item.name}</div>
            {item.id.length > 0 && <CopyToClipboardButton copyText={item.id} label="Copy ID" />}
          </>
        );
      },
    },
    {
      key: 'type',
      render: item => {
        return (
          <BubbleBadge className="mt-1 mr-1" color={hashStringToColor(item.type)}>
            {item.type}
          </BubbleBadge>
        );
      },
    },
    {
      key: 'access',
      render: item => (
        <Tooltip text={'Click to manage the access'}>
          <AccessGlanceButton item={item._source} onClick={() => setShowAccessForm(item._source)} />
        </Tooltip>
      ),
    },
    {
      key: 'tags',
      render: item =>
        item.tags.length > 0 && (
          <>
            {item.tags.map(t => (
              <BubbleBadge key={t._id} className="mt-1 mr-1" color={hashStringToColor(t.name)}>
                {t.name}
              </BubbleBadge>
            ))}
          </>
        ),
    },
    {
      key: 'endpoints',
      render: item => <McmEndpoints template={item._source} />,
    },
    {
      key: 'value',
      render: itm => {
        if (isNotBasicContentTemplate(itm._source)) {
          return <div>{stripHTML(itm._source.description)}</div>;
        }

        return <div>{stripHTML(itm.value)}</div>;
      },
    },
    {
      key: 'createdAt',
      render: item => {
        let element = <></>;

        if (item._source.createdAt) {
          const createdAt = getLocaleUTCDateTimeString(new Date(item._source.createdAt));
          const [date, time] = createdAt.split(', ');
          element = (
            <>
              <div className="block whitespace-nowrap">{date}</div>
              <div className="block whitespace-nowrap">{time}</div>
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'actions',
      render: item => (
        <>
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            icon="edit"
            onClick={() => setMode({ type: 'edit', edit: item._source })}
          />

          {canManageLibraries(user) && (
            <BubbleButton
              className="p-2-i"
              variant="slate"
              inverse
              icon="copy"
              onClick={() => setMode({ type: 'create', edit: item._source })}
            />
          )}
          {canModifyMultiPropertyItem(user, serverInfo, property._id, item._source) && (
            <>
              {!showDelete[item.id] && (
                <BubbleButton
                  className="p-2-i"
                  variant="dirt"
                  inverse
                  onClick={() => {
                    setShowDelete({ ...showDelete, [item.id]: true });
                  }}
                  icon="archive"
                />
              )}
              {showDelete[item.id] && (
                <>
                  <BubbleButton
                    className="p-2-i"
                    variant="dirt"
                    inverse
                    onClick={() => handleFormArchive(item._source, !item._source.archived)}
                    icon={findIconDefinition({
                      prefix: 'fak',
                      iconName: 'solid-box-archive-circle-check',
                    })}
                  />
                  <BubbleButton
                    className="p-2-i"
                    variant="slate"
                    inverse
                    onClick={() => {
                      setShowDelete({ ...showDelete, [item.id]: false });
                    }}
                    icon={findIconDefinition({
                      prefix: 'fak',
                      iconName: 'solid-box-archive-circle-xmark',
                    })}
                  />
                </>
              )}
            </>
          )}
        </>
      ),
    },
  ];

  function headerFn() {
    return <LibrariesSubNavigation active="content" />;
  }

  function bodyFn() {
    return (
      <>
        <BubbleTable
          responsive
          striped
          headVariant="titanium"
          fields={tableFields}
          items={dataBundle?.tableItems}
          keyExtractor={item => item.id}
          stickyHeader={182}
          templates={tableTemplates}
          showPretitle
          pretitleClassName="p-0-i"
          showAllItems={true}
          onRowDblClick={itm => {
            setMode({ type: 'edit', edit: itm._source });
          }}
          pretitleRender={() => (
            <div className="px-4 pt-4 pb-1">
              <BubbleDebounce
                onTrigger={[
                  (search: string) => setPaginationParams({ ...paginationParams, page: 1, search }),
                  500,
                ]}
              >
                {({ debounce }) => (
                  <BubbleInput
                    type="text"
                    variant="titanium"
                    block
                    value={paginationParams.search}
                    inputClassName="outline-none-i"
                    onValue={debounce}
                    placeholder="Search..."
                  />
                )}
              </BubbleDebounce>
            </div>
          )}
        />

        {paginationMetadata && (
          <BubbleTablePaginationFooter
            paginationMetadata={paginationMetadata}
            perPage={paginationParams.perPage}
            onLoadMoreClick={() =>
              setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
            }
            onPerPageChange={value =>
              setPaginationParams({ ...paginationParams, page: 1, perPage: value })
            }
          />
        )}
      </>
    );
  }

  function outerFn() {
    return (
      <>
        {mode && dataBundle && (
          <BubbleDrawer containerClassName="bg-eggshell">
            <BubbleDrawerBasicContainer
              title={`${mode.type === 'edit' ? 'Edit' : 'Create'} Content`}
              subtitle="Content Details"
              onClose={() => setMode(undefined)}
            >
              <ContentTemplateFormik
                mode={mode}
                onSubmit={onSubmit}
                actionsAppearance="drawer-float"
                subnavClassName="-mx-8"
              />
            </BubbleDrawerBasicContainer>
          </BubbleDrawer>
        )}
        {showAccessForm && (
          <BubbleDrawer
            onClose={() => setShowAccessForm(undefined)}
            containerClassName="bg-eggshell"
          >
            <BubbleDrawerBasicContainer
              title="Edit Content"
              subtitle="Manage Access"
              onClose={() => setShowAccessForm(undefined)}
            >
              <PropertyAccessFormik
                edit={showAccessForm}
                makePropertyAccessItem={itm => ({ property: itm })}
                updateFn={update}
                actionsAppearance="drawer-float"
                subnavClassName="-mx-8"
                onSubmit={onSubmit}
              />
            </BubbleDrawerBasicContainer>
          </BubbleDrawer>
        )}
      </>
    );
  }

  function sideFn() {
    return (
      <>
        <div className="flex flex-col relative items-stretch p-4 bg-titanium-darker">
          <div className="grid gap-2">
            {canManageLibraries(user) && (
              <BubbleButton
                variant="lime"
                onClick={handleAddContentClick}
                className="text-left"
                icon="sparkles"
              >
                Create Content
              </BubbleButton>
            )}
            <BubbleButton
              variant="slate"
              onClick={generateCSV}
              className="text-left"
              icon="file-export"
            >
              Export to CSV
            </BubbleButton>
          </div>
        </div>
        <BubbleAccordion
          variant="titanium"
          className="flex-grow flex flex-col border-t border-forest-darkest"
          labels={['Filters']}
          open={[0]}
          locked={[0]}
        >
          <div className="grid gap-4 p-4">
            <BubbleFormGroup label="Dates" labelClassName="text-white-i">
              <div className="grid grid-cols-1 gap-1">
                <BubbleDebounce
                  onTrigger={[
                    (date: string) => {
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        startDate: date,
                      });
                    },
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.startDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
                <BubbleDebounce
                  onTrigger={[
                    (date: string) =>
                      setPaginationParams({ ...paginationParams, page: 1, endDate: date }),
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.endDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
              </div>
            </BubbleFormGroup>
            <BubbleFormGroup label="Type" labelClassName="text-white-i">
              <BubbleTypeahead
                block
                variant="titanium"
                placeholder="Filter types shown..."
                menuClassName="max-h-80 overflow-y-auto"
                value={filters.types}
                options={typeOpts}
                onAdd={t => {
                  const types = [...filters.types, t];

                  setFilters({ ...filters, types });
                  setPaginationParams({ ...paginationParams, page: 1, types });
                }}
                onRemove={t => {
                  const types = filters.types.filter(t2 => t.key !== t2.key);

                  setFilters({ ...filters, types });
                  setPaginationParams({ ...paginationParams, page: 1, types });
                }}
                onClearAll={() => {
                  setFilters({ ...filters, types: [] });
                  setPaginationParams({ ...paginationParams, page: 1, types: [] });
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Tags" labelClassName="text-white-i">
              <BubbleTypeahead
                block
                variant="titanium"
                placeholder="Filter tags shown..."
                menuClassName="max-h-80 overflow-y-auto"
                labelExtractor={opt => opt.name}
                value={filters.tags}
                options={tagOpts}
                onAdd={t => {
                  const tags = [...filters.tags, t];

                  setFilters({ ...filters, tags });
                  setPaginationParams({ ...paginationParams, page: 1, tags });
                }}
                onRemove={t => {
                  const tags = filters.tags.filter(t2 => t.key !== t2.key);

                  setFilters({ ...filters, tags });
                  setPaginationParams({ ...paginationParams, page: 1, tags });
                }}
                onClearAll={() => {
                  setFilters({ ...filters, tags: [] });
                  setPaginationParams({ ...paginationParams, page: 1, tags: [] });
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Options" labelClassName="text-white-i">
              <div className="grid gap-1">
                <div className="text-white flex relative">
                  <BubbleInput
                    type="checkbox"
                    variant="titanium"
                    checked={paginationParams.archived}
                    inputClassName="stretched-link"
                    className="mr-1"
                    onValue={(checked: boolean) => {
                      setPaginationParams({ ...paginationParams, page: 1, archived: checked });
                    }}
                  />
                  <span>View Archived</span>
                </div>
              </div>
            </BubbleFormGroup>
          </div>
        </BubbleAccordion>
      </>
    );
  }

  return (
    <Libraries
      user={user}
      active="libraries"
      property={property}
      loading={loading}
      contentRef={contentRef}
      headerFn={headerFn}
      bodyFn={bodyFn}
      outerFn={outerFn}
      sideFn={sideFn}
      {...rest}
    />
  );
}
